import React from 'react';
import { Button } from './Button';

interface PageHeaderProps {
  title: React.ReactNode;
  /** One line under the title. Kept to a readable measure. */
  description?: React.ReactNode;
  /** Buttons, filters or chips that belong to the page as a whole. */
  actions?: React.ReactNode;
  /** When set, a tertiary "back" button renders above the title. */
  backLabel?: string;
  onBack?: () => void;
  className?: string;
}

/**
 * The one page title row. Every routed page opens with this so the title,
 * its description and the page-level actions sit at the same place with the
 * same spacing everywhere; a page may add actions, never restyle the title.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  actions,
  backLabel,
  onBack,
  className = '',
}) => (
  <header
    data-testid="page-header"
    className={`mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${className}`.trim()}
  >
    <div className="min-w-0">
      {onBack && (
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={onBack}
          className="mb-2 -ml-3 !text-signature hover:!text-signature-strong"
        >
          {backLabel ?? 'Back'}
        </Button>
      )}
      <h1 className="heading-2">{title}</h1>
      {description && <p className="mt-1 max-w-[70ch] text-ink-secondary">{description}</p>}
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </header>
);
